import {
  BlockType,
  CreateBlockType,
  UpdateBlockType,
  UpdateRecurrenceBlockType,
} from "../../types/block/type";
import { addDays } from "../../utils/date-format/dateUtils";

/**
 * * byday rule이 있을 경우 start가 byday 요일에 포함되었는지 확인
 * @param payload
 * @returns
 */

export const shouldCorrectDate = (
  payload: CreateBlockType | UpdateBlockType | UpdateRecurrenceBlockType
) => {
  const isRecurrence = Boolean(payload.recurrence && payload.recurrence.length > 0);
  const isIncludeByDay = Boolean(payload.recurrence && payload.recurrence[0].includes("BYDAY"));

  return isRecurrence && isIncludeByDay;
};

/**
 * 전체 로직: 반복 블록의 경우 시작 날짜를 가장 가까운 미래의 요일로 변경
 * recurrence rule에 byday 값이 있는지 shouldCorrectDate를 통해 확인된 로직만 아래 함수 적용
 *
 * 1. byday에 start에 해당하는 요일이 포함되지 않았을 경우 가장 가까운 요일로 변경
 *    - 변경하기 위해 가장 자까운 요일 찾기
 *    - 해당 요일로 start 날짜 변경 (시간은 그대로)
 *    - 바뀐 start에 기존 start-end diff 차이를 더해 바뀐 end 설정
 * 2. 조정된 start와 end를 반환
 *
 * @param {*} recurrence
 * @param {*} start
 * @param {*} end
 * @returns
 */

const convertByDayToNumbers = (recurrence: string): number[] => {
  const byDayMatchDayOfWeek = recurrence.match(/BYDAY=([^;]+)/)![1];
  return byDayMatchDayOfWeek.split(",").map((day) => {
    const dayMap = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
    return dayMap[day as keyof typeof dayMap];
  });
};

export const isStartDayIncludedInByDay = (recurrence: string, startDate: Date): boolean => {
  const byDay = convertByDayToNumbers(recurrence);
  return byDay.includes(startDate.getDay());
};

export const correctDateToNextByDay = (recurrence: string, start: Date, end: Date) => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  const byDay = convertByDayToNumbers(recurrence);
  const startDay = startDate.getDay();

  // NOTE 가장 가까운 byday 요일 찾기
  const nearestDay = byDay.reduce((acc, curr) => {
    // NOTE 과거에 있는 요일은 다음주를 기준으로 계산하여 +7을 해서 계산(절댓값 X), 요일이기 때문에 %7 연산
    // Byday에 있는 요일을 순회하며 오늘과 가장 차이가 적은 날 찾기
    const diff = (curr - startDay + 7) % 7;
    // NOTE diff 가장 작은 차이를 가진 요일 찾기
    return diff < (acc - startDay + 7) % 7 ? curr : acc;
  });

  // NOTE 찾은 가장 가까운 요일까지의 날짜 차이 계산
  const daysToAdd = (nearestDay - startDay + 7) % 7;
  // NOTE 기존 start-end 차이 계산
  const timeDiff = endDate.getTime() - startDate.getTime();

  const adjustedStart = addDays(startDate, daysToAdd);
  const adjustedEnd = new Date(adjustedStart.getTime() + timeDiff);

  return { start: adjustedStart, end: adjustedEnd };
};

/**
 * 요일 구하기
 * @param date
 * @returns
 */
export const getDayOfWeekForRRULE = (date: Date): string => {
  const days = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
  return days[date.getDay()];
};

/**
 * 주간 반복 일정에 BYDAY로 요일 추가
 * @param blockStart
 * @returns
 */
const addByDayToWeeklyRRULE = ({ blockStart }: { blockStart: Date }) => {
  const dayOfWeek = getDayOfWeekForRRULE(blockStart);

  return `RRULE:FREQ=WEEKLY;BYDAY=${dayOfWeek}`;
};

/**
 * payload에 반복룰이 있고, 반복룰이 RRULE:FREQ=WEEKLY인 경우 처리
 * @param payload
 * @param prevBlockData
 * @param updateBlockData
 * @returns
 */
export const convertPayloadWeeklyRecurrenceToByDay = ({
  payload,
  prevBlockData,
  updateBlockData,
}: {
  payload: CreateBlockType | UpdateBlockType | UpdateRecurrenceBlockType;
  prevBlockData: BlockType;
  updateBlockData: BlockType;
}) => {
  // 반복 일정이 없는 경우 종료
  const isRecurrence = Boolean(payload.recurrence && payload.recurrence.length > 0);
  if (!isRecurrence) return payload;

  // 반복 일정이 있고, RRULE:FREQ=WEEKLY인 경우 처리

  const isSameWeekly = payload.recurrence && payload.recurrence[0] === "RRULE:FREQ=WEEKLY";
  if (isSameWeekly && updateBlockData.start && updateBlockData.end) {
    // NOTE : 기존 블록 대응
    // prevBlockData가 있으면서, 반복룰이 "RRULE:FREQ=WEEKLY"인 경우 해당 룰 그대로 적용
    if (prevBlockData.recurrence && prevBlockData.recurrence[0] === "RRULE:FREQ=WEEKLY") {
      return payload;
    }

    const startDate = payload.start ? new Date(payload.start) : new Date(updateBlockData.start);

    const newRecurrence = addByDayToWeeklyRRULE({ blockStart: startDate });
    const newPayload = {
      ...payload,
      recurrence: [newRecurrence],
    };
    return newPayload;
  }

  return payload;
};
